import type {
  OrganizationDetails,
  OrganizationSummary,
} from '@raas/cornerstone-auth/client';
import { useOrganizationState } from '@raas/cornerstone-auth/client';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext } from 'react';
import { signOut, useSession } from './auth';

interface AppSessionValue {
  activeOrganization: OrganizationDetails | OrganizationSummary | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSwitchingOrganization: boolean;
  logout(): Promise<void>;
  memberRole: 'owner' | 'admin' | 'member' | null;
  orgId: string | null;
  organizations: OrganizationSummary[];
  refreshOrganizationState(): Promise<void>;
  refreshSession(): Promise<void>;
  session: ReturnType<typeof useSession>['data'] | null;
  switchOrganization(organizationId: string): Promise<void>;
}

const AppSessionContext = createContext<AppSessionValue | null>(null);

export function AppSessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const sessionQuery = useSession();
  const session = sessionQuery.data ?? null;
  const organizationState = useOrganizationState({
    refetchSession: () => sessionQuery.refetch(),
    session,
  });

  async function logout() {
    await signOut();
    queryClient.clear();
    window.location.href = '/login';
  }

  return (
    <AppSessionContext.Provider
      value={{
        activeOrganization: organizationState.activeOrganization,
        isAuthenticated: !!session,
        isLoading: sessionQuery.isPending || organizationState.isLoading,
        isSwitchingOrganization: organizationState.isSwitchingOrganization,
        logout,
        memberRole: organizationState.memberRole,
        orgId: organizationState.orgId,
        organizations: organizationState.organizations,
        refreshOrganizationState: organizationState.refreshOrganizationState,
        refreshSession: async () => {
          await sessionQuery.refetch();
        },
        session,
        switchOrganization: organizationState.switchOrganization,
      }}
    >
      {children}
    </AppSessionContext.Provider>
  );
}

export function useAppSession() {
  const value = useContext(AppSessionContext);
  if (!value) {
    throw new Error('useAppSession must be used within AppSessionProvider');
  }
  return value;
}
